import { useAuth } from "@dl/web-core-js/auth";
import { Button, Dialog, Field, Input, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductionTransferClient, type ProductionTransferDTO } from "../../api/floor";
import { formatNumber, refusalMessage } from "../../api/manufacturing";

/**
 * Receives a truck at Terrebonne: each line counted, starting at what was
 * loaded, with a note for what is short. What is short broke on the road
 * (bris de transport): the complete sets that arrived go into Terrebonne's
 * stock and to the assembly orders, by the split. Given a truck, it receives
 * that one; given only the production order, it offers its trucks still on
 * their way.
 */
export function ReceiveTransferDialog({
  lotId,
  lotNumber,
  transfer,
  onClose,
  onReceived,
}: {
  lotId: number;
  lotNumber: number;
  transfer?: ProductionTransferDTO;
  onClose: () => void;
  onReceived: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ProductionTransferClient(http), [http]);
  const open = useQuery({
    queryKey: ["production", "production-transfer", "lot", lotId, "shipped"],
    queryFn: async () => (await client.forLot(lotId)).filter((one) => one.status === "SHIPPED"),
    enabled: transfer == null,
  });
  const [chosenId, setChosenId] = useState<number | null>(null);
  const [counts, setCounts] = useState<Record<number, { quantity: string; note: string }>>({});
  const list = transfer ? [transfer] : (open.data ?? []);
  const chosen = list.find((one) => one.id === chosenId) ?? list[0];

  const counted = (lineId: number, shipped: number) => counts[lineId] ?? { quantity: String(Number(shipped)), note: "" };
  const valid = chosen != null && chosen.lines.every((line) => {
    const count = counted(line.id, line.quantity_shipped);
    return count.quantity.trim() !== "" && Number(count.quantity) >= 0;
  });

  const receive = useMutation({
    mutationFn: () =>
      client.receive(
        chosen!.id,
        chosen!.lines.map((line) => {
          const count = counted(line.id, line.quantity_shipped);
          return { production_transfer_line_id: line.id, quantity_received: Number(count.quantity), note: count.note.trim() || null };
        }),
      ),
    onSuccess: async (received) => {
      toast.success(t("lot.transfer.received-toast", "Transfer {{n}} received", { n: received.number }));
      await onReceived();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title={
        chosen && transfer
          ? t("lot.transfer.receive-title", "Receive transfer {{n}}", { n: chosen.number })
          : t("tb.receive.title", "Receive lot {{n}}", { n: lotNumber })
      }
      description={t(
        "lot.transfer.receive.description.v2",
        "Count what came off the truck. What is short broke on the road (bris de transport): only the complete sets that arrived go to the assembly orders.",
      )}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={receive.isPending} onClick={() => receive.mutate()}>
            {t("lot.transfer.receive", "Receive")}
          </Button>
        </>
      }
    >
      {transfer == null && open.isLoading ? (
        <p className="text-[13px] text-ink-muted">{t("common.loading", "Loading…")}</p>
      ) : !chosen ? (
        <p className="text-[13px] text-ink-muted">{t("tb.receive.none", "No truck is on its way for this lot.")}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {list.length > 1 && (
            <Field label={t("tb.receive.truck", "Truck")} htmlFor="receive-truck">
              <Select
                id="receive-truck"
                value={String(chosen.id)}
                options={list.map((one) => ({
                  value: String(one.id),
                  label: `#${String(one.number)} · ${one.shipped_date ?? ""}${one.truck_reference ? ` · ${one.truck_reference}` : ""}`,
                }))}
                onChange={(event) => setChosenId(Number(event.target.value))}
              />
            </Field>
          )}
          <div className="max-h-[55vh] overflow-auto rounded-md border border-line">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-surface-sunk text-left text-[11px] uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-3 py-1.5 font-medium">{t("lot.component.code", "Component")}</th>
                  <th className="px-3 py-1.5 text-right font-medium">{t("lot.transfer.shipped", "Shipped")}</th>
                  <th className="px-3 py-1.5 font-medium">{t("lot.transfer.counted", "Counted")}</th>
                  <th className="px-3 py-1.5 font-medium">{t("column.note", "Note")}</th>
                </tr>
              </thead>
              <tbody>
                {chosen.lines.map((line) => {
                  const count = counted(line.id, line.quantity_shipped);
                  const short = Number(count.quantity) < Number(line.quantity_shipped);
                  return (
                    <tr key={line.id} className="border-t border-line">
                      <td className="px-3 py-1">
                        <span className="font-mono text-[12px]">{line.component_code}</span>
                        {line.letter && <span className="ml-2 text-ink-muted">{line.letter}</span>}
                        <span className="ml-2 text-[11px] text-ink-faint">{line.part_number}</span>
                      </td>
                      <td className="px-3 py-1 text-right tabular-nums">{formatNumber(line.quantity_shipped)}</td>
                      <td className="px-3 py-1">
                        <Input
                          aria-label={t("lot.transfer.counted", "Counted")}
                          type="number"
                          min="0"
                          step="1"
                          className={`h-7 w-24 py-0 ${short ? "text-warn" : ""}`}
                          value={count.quantity}
                          invalid={count.quantity.trim() === "" || Number(count.quantity) < 0}
                          onChange={(event) => setCounts({ ...counts, [line.id]: { ...count, quantity: event.target.value } })}
                        />
                      </td>
                      <td className="px-3 py-1">
                        <Input
                          aria-label={t("column.note", "Note")}
                          className="h-7 py-0"
                          value={count.note}
                          placeholder={short ? t("lot.transfer.damage", "Bris de transport") : undefined}
                          onChange={(event) => setCounts({ ...counts, [line.id]: { ...count, note: event.target.value } })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Dialog>
  );
}
